import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AppLayout } from "../components/tabon/AppLayout";
import { formatDate, statusColor } from "../lib/format";
import {
  Beer, Users, AlertTriangle, Activity, Calendar, TrendingUp, Lock, ShieldAlert, ChevronDown, ChevronUp,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const BASE_CARDS = [
  { key: "new_this_week", label: "New Customers", icon: Users, color: "text-emerald-400" },
  { key: "visits_this_month", label: "Visits", icon: TrendingUp, color: "text-sky-400" },
  { key: "at_risk", label: "Customers At Risk", icon: AlertTriangle, color: "text-[#EF4444]" },
  { key: "total_customers", label: "Total Customers", icon: Activity, color: "text-white" },
];

const BOTTLE_CARDS = [
  { key: "active_tabs", label: "Active Tabs", icon: Beer, color: "text-[#9D00FF]", panel: "active" },
  { key: "expiring_soon", label: "Expiring in 7 Days", icon: Calendar, color: "text-[#F59E0B]", panel: "expiring" },
];

function buildRangeParams(range, customFrom, customTo) {
  const now = new Date();
  const pad = (d) => d.toISOString().slice(0, 10);
  if (range === "today") {
    return { from_date: pad(now), to_date: pad(now) };
  }
  if (range === "yesterday") {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    return { from_date: pad(y), to_date: pad(y) };
  }
  if (range === "week") {
    const w = new Date(now); w.setDate(w.getDate() - 7);
    return { from_date: pad(w), to_date: pad(now) };
  }
  if (range === "month") {
    const m = new Date(now); m.setDate(1);
    return { from_date: pad(m), to_date: pad(now) };
  }
  if (range === "custom" && customFrom && customTo) {
    return { from_date: customFrom, to_date: customTo };
  }
  return {};
}

function groupByBrand(tabs) {
  const map = {};
  tabs.forEach(t => {
    if (!map[t.brand]) map[t.brand] = [];
    map[t.brand].push(t);
  });
  return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
}

function MlBar({ remainingMl, bottleSizeMl, status }) {
  const pct = bottleSizeMl > 0 ? Math.min(100, Math.round((remainingMl / bottleSizeMl) * 100)) : 0;
  const fill = status === "Expiring Soon" ? "#F59E0B" : "#9D00FF";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden min-w-[40px]">
        <div className="h-1 rounded-full transition-all" style={{ width: `${pct}%`, background: fill }} />
      </div>
      <span className="text-xs text-[#A0A0A0] min-w-[48px] text-right">{remainingMl}ml</span>
    </div>
  );
}

function VerifiedCell({ tab }) {
  if (tab.otp_verified) {
    return (
      <span title="OTP verified" className="inline-flex items-center gap-1 text-emerald-400 text-xs">
        <Lock size={11} /> Verified
      </span>
    );
  }
  if (tab.otp_bypassed) {
    return (
      <span title={`Manager override by ${tab.otp_bypassed_by || "manager"}`} className="inline-flex items-center gap-1 text-amber-400 text-xs">
        <ShieldAlert size={11} /> Override
      </span>
    );
  }
  return <span className="text-xs text-[#A0A0A0]">—</span>;
}

function BrandGroup({ brand, tabs, showStatus, hideHeader }) {
  const combinedMl = tabs.reduce((sum, t) => sum + (t.remaining_ml || 0), 0);
  const expiringCount = tabs.filter(t => t.status === "Expiring Soon").length;
  return (
    <div className="mb-4 last:mb-0">
      {!hideHeader && (
      <div className="flex items-center justify-between bg-[#0A0A0A] rounded-lg px-3 py-2 mb-1">
        <span className="text-sm font-bold text-white">{brand}</span>
        <div className="flex items-center gap-2">
          {showStatus ? (
            <span className="text-[10px] px-2 py-0.5 rounded border font-bold uppercase tracking-wider bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40">
              {expiringCount} expiring
            </span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded border font-bold uppercase tracking-wider bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40">
              {tabs.length} tab{tabs.length !== 1 ? "s" : ""}
            </span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded border font-bold text-[#A0A0A0] border-white/10 bg-white/5">
            {combinedMl}ml combined
          </span>
        </div>
      </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[#A0A0A0] text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left py-2 pr-3">Customer</th>
              <th className="text-left py-2 pr-3">Phone</th>
              <th className="text-left py-2 pr-3">Remaining</th>
              <th className="text-left py-2 pr-3">Expiry</th>
              {showStatus && <th className="text-left py-2 pr-3">Status</th>}
              <th className="text-left py-2">Verified</th>
            </tr>
          </thead>
          <tbody>
            {tabs.map((t) => (
              <tr key={t.id} className="border-t border-white/5">
                <td className="py-2.5 pr-3 font-medium text-white">{t.customer?.name || "—"}</td>
                <td className="py-2.5 pr-3 text-[#A0A0A0]">{t.customer?.phone || "—"}</td>
                <td className="py-2.5 pr-3 min-w-[120px]">
                  <MlBar remainingMl={t.remaining_ml} bottleSizeMl={t.bottle_size_ml} status={t.status} />
                </td>
                <td className={`py-2.5 pr-3 text-sm ${t.status === "Expiring Soon" ? "text-[#F59E0B]" : "text-[#A0A0A0]"}`}>
                  {formatDate(t.expiry_at)}
                </td>
                {showStatus && (
                  <td className="py-2.5 pr-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${statusColor(t.status)}`}>
                      {t.status}
                    </span>
                  </td>
                )}
                <td className="py-2.5">
                  <VerifiedCell tab={t} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DrillDownPanel({ title, tabs, accentColor, borderColor, onClose, showStatus }) {
  const groups = groupByBrand(tabs);
  const totalMl = tabs.reduce((sum, t) => sum + (t.remaining_ml || 0), 0);
  const [expandedBrands, setExpandedBrands] = React.useState({});
  const toggleBrand = (brand) => setExpandedBrands(prev => ({ ...prev, [brand]: !prev[brand] }));
  return (
    <div className={`bg-[#1A1A1A] border rounded-xl p-5 mt-4 ${borderColor}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-white">{title}</h2>
          <p className="text-xs text-[#A0A0A0] mt-0.5">
            {tabs.length} tab{tabs.length !== 1 ? "s" : ""} · {totalMl}ml combined
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-[#A0A0A0] hover:text-white text-xs border border-white/10 rounded-lg px-3 py-1.5 transition"
        >
          Close ↑
        </button>
      </div>
      {tabs.length === 0 ? (
        <p className="text-sm text-[#A0A0A0]">No tabs to display.</p>
      ) : (
        groups.map(([brand, brandTabs]) => (
          <div key={brand}>
            <button
              onClick={() => toggleBrand(brand)}
              className="w-full flex items-center justify-between bg-[#0A0A0A] rounded-lg px-3 py-2 mb-1 hover:bg-white/5 transition"
            >
              <span className="text-sm font-bold text-white">{brand}</span>
              <div className="flex items-center gap-2">
                {showStatus ? (
                  <span className="text-[10px] px-2 py-0.5 rounded border font-bold uppercase tracking-wider bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40">
                    {brandTabs.filter(t => t.status === "Expiring Soon").length} expiring
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded border font-bold uppercase tracking-wider bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40">
                    {brandTabs.length} tab{brandTabs.length !== 1 ? "s" : ""}
                  </span>
                )}
                <span className="text-[10px] px-2 py-0.5 rounded border font-bold text-[#A0A0A0] border-white/10 bg-white/5">
                  {brandTabs.reduce((sum, t) => sum + (t.remaining_ml || 0), 0)}ml
                </span>
                {expandedBrands[brand]
                  ? <ChevronUp size={14} className="text-[#A0A0A0]" />
                  : <ChevronDown size={14} className="text-[#A0A0A0]" />}
              </div>
            </button>
            {expandedBrands[brand] && (
              <BrandGroup brand={brand} tabs={brandTabs} showStatus={showStatus} hideHeader />
            )}
          </div>
        ))
      )}
    </div>
  );
}

const money = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const H2 = ({ children }) => <h2 className="text-sm font-bold uppercase tracking-wider text-[#A0A0A0] mb-3">{children}</h2>;

function StatTile({ label, value, sub, accent = "text-white" }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
      <div className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">{label}</div>
      <div className={`text-3xl font-black ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-[#A0A0A0] mt-1">{sub}</div>}
    </div>
  );
}

// The ordering dashboard (AF-6) — the restaurant owner's morning screen. Reuses the day summary
// (/reports/day-summary: net sales, order split, AOV, payment mix, expected-in-drawer, exceptions),
// top items (/reports/item-sales) and the live numbers (/dashboard/ordering-live) — no second
// reporting path.
function OrderingDashboard({ daySummary, topItems, liveData, navigate }) {
  const s = daySummary?.sales || {};
  const net = Number(s.net_collected || 0);
  const lastWeek = liveData?.last_week_net_collected;
  const delta = (lastWeek != null && lastWeek > 0) ? Math.round(((net - lastWeek) / lastWeek) * 100) : null;
  const weekday = new Date().toLocaleDateString("en-IN", { weekday: "long" });
  const split = daySummary?.order_type_split || {};
  const splitLabel = (k) => k === "dine_in" ? "dine-in" : k === "room" ? "room" : "takeaway";
  const pay = daySummary?.payment_breakdown || {};
  const expected = daySummary?.cash?.expected_cash;
  const exc = daySummary?.exceptions || {};
  const openBills = liveData?.open_bills || {};
  const oldest = liveData?.oldest_ticket_age_min;
  const tables = liveData?.tables || {};
  const items = (topItems?.items || []).slice(0, 5);
  const excRows = [
    ["Cancelled", daySummary?.cancelled?.count],
    ["Discounts", exc.discounts_given?.count],
    ["Voids", exc.bills_voided?.count],
    ["Closed unpaid", exc.bills_marked_unpaid?.count],
  ];

  return (
    <div className="space-y-6 mb-8">
      <section>
        <H2>Today so far</H2>
        <div className="grid sm:grid-cols-3 gap-4">
          <StatTile
            label="Net sales"
            value={money(net)}
            accent="text-[#9D00FF]"
            sub={delta == null ? "no same day last week" : `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta)}% vs last ${weekday}`}
          />
          <StatTile
            label="Orders"
            value={s.order_count ?? 0}
            sub={Object.entries(split).map(([k, v]) => `${v.count} ${splitLabel(k)}`).join(" · ") || "—"}
          />
          <StatTile label="Avg order value" value={money(s.avg_order_value || 0)} />
        </div>
      </section>

      <section>
        <H2>Right now</H2>
        <div className="grid sm:grid-cols-3 gap-4">
          <StatTile label="Open bills" value={openBills.count ?? 0} sub={`${money(openBills.value || 0)} on the floor`} />
          <StatTile
            label="Oldest ticket"
            value={oldest == null ? "—" : `${oldest} min`}
            sub="in the kitchen"
            accent={oldest != null && oldest >= 15 ? "text-[#F59E0B]" : "text-white"}
          />
          <div onClick={() => navigate("/tables")} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 cursor-pointer hover:border-[#9D00FF]/40 transition">
            <div className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">Tables occupied</div>
            <div className="text-3xl font-black text-white">{tables.occupied ?? 0}<span className="text-lg text-[#A0A0A0]"> / {tables.total ?? 0}</span></div>
            <div className="text-xs text-[#9D00FF] mt-1">View tables →</div>
          </div>
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
          <H2>Payment mix</H2>
          {Object.keys(pay).length === 0 ? (
            <div className="text-sm text-[#555]">No payments collected yet.</div>
          ) : Object.entries(pay).map(([m, v]) => (
            <div key={m} className="flex justify-between text-sm py-1">
              <span className="capitalize text-[#A0A0A0]">{m}</span>
              <span className="text-white">{money(v.amount)} <span className="text-[#A0A0A0]">· {v.count}</span></span>
            </div>
          ))}
          {expected != null && (
            <div className="flex justify-between text-sm py-2 mt-2 border-t border-white/10">
              <span className="text-[#A0A0A0]">Expected in drawer</span>
              <span className="font-bold text-white">{money(expected)}</span>
            </div>
          )}
        </section>
        <section className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
          <H2>Top items today</H2>
          {items.length === 0 ? (
            <div className="text-sm text-[#555]">Nothing sold yet.</div>
          ) : items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm py-1 gap-3">
              <span className="text-white truncate min-w-0">{i + 1}. {it.name}</span>
              <span className="text-[#A0A0A0] shrink-0">{it.qty}</span>
            </div>
          ))}
        </section>
      </div>

      <section>
        <H2>Watch-outs</H2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {excRows.map(([label, count]) => (
            <StatTile key={label} label={label} value={count ?? 0} accent={(count || 0) > 0 ? "text-[#F59E0B]" : "text-white"} />
          ))}
        </div>
      </section>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [activeTabs, setActiveTabs] = useState([]);
  const [daySummary, setDaySummary] = useState(null);   // ordering: today's /reports/day-summary
  const [topItems, setTopItems] = useState(null);        // ordering: /reports/item-sales (today)
  const [liveData, setLiveData] = useState(null);        // ordering: /dashboard/ordering-live
  const [openPanel, setOpenPanel] = useState(null);
  const [dateRange, setDateRange] = useState("week");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const navigate = useNavigate();
  const { hasFeature } = useAuth();
  // Render by ENABLED feature, and never fetch data for a feature that is off (AF-6). The flags come
  // from the venue (hasFeature, synchronously) — not from a /dashboard/stats call we'd fire only to
  // learn them. hasFeature reads venue.features with the SAME defaults the backend's stats endpoint
  // uses (loyalty_points and bottle_locker both default on when a venue hasn't set the key), so this
  // matches what the dashboard showed before, without the round-trip.
  const orderingOn = hasFeature("ordering");
  const loyaltyOn = hasFeature("loyalty_points");
  const bottleLockerOn = hasFeature("bottle_locker");

  // Loyalty / customer tiles — only when loyalty or the bottle locker (which share /dashboard/stats)
  // is on. A pure ordering venue never fires this.
  useEffect(() => {
    if (!loyaltyOn && !bottleLockerOn) return;
    const params = buildRangeParams(dateRange, customFrom, customTo);
    api.get("/dashboard/stats", { params }).then(({ data }) => setStats(data)).catch(() => {});
  }, [dateRange, customFrom, customTo, loyaltyOn, bottleLockerOn]);

  // Bottle-locker data — only when that feature is on (was fired for every venue, Vinod included).
  useEffect(() => {
    if (!bottleLockerOn) return;
    api.get("/dashboard/expiring-tabs").then(({ data }) => setExpiring(data)).catch(() => {});
    api.get("/dashboard/active-tabs").then(({ data }) => setActiveTabs(data)).catch(() => {});
  }, [bottleLockerOn]);

  // Ordering dashboard — today's figures, reusing the reports' own computations (no second path). The
  // live endpoint is the authority on which business DATE "today" is (it honours the venue's past-
  // midnight rollover); day-summary and item-sales treat their date args as business dates too, so we
  // drive them off that one date and all three halves describe the same day. If live can't be reached
  // we fall back to the IST calendar date — exactly right for a midnight-rollover venue — so a single
  // hiccup never blanks the whole panel.
  useEffect(() => {
    if (!orderingOn) return;
    let off = false;
    const istToday = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });  // YYYY-MM-DD IST
    const loadReports = (bd) => {
      api.get("/reports/day-summary", { params: { date: bd } }).then(({ data }) => { if (!off) setDaySummary(data); }).catch(() => {});
      api.get("/reports/item-sales", { params: { from: bd, to: bd } }).then(({ data }) => { if (!off) setTopItems(data); }).catch(() => {});
    };
    api.get("/dashboard/ordering-live")
      .then(({ data }) => { if (off) return; setLiveData(data); loadReports(data?.business_date || istToday); })
      .catch(() => { if (!off) loadReports(istToday); });
    return () => { off = true; };
  }, [orderingOn]);

  const togglePanel = (key) => setOpenPanel(prev => prev === key ? null : key);

  const visibleCards = [
    ...(bottleLockerOn ? BOTTLE_CARDS : []),
    ...(loyaltyOn ? BASE_CARDS : []),   // the customer/loyalty tiles — not for an ordering-only venue (AF-6)
  ];

  return (
    <AppLayout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">Dashboard</h1>
            <p className="text-sm text-[#A0A0A0]">At-a-glance performance of your venue</p>
          </div>
          {/* The range selector drives the loyalty/customer tiles; the ordering panels are always
              today, so an ordering-only venue doesn't see it (AF-6). */}
          {loyaltyOn && (
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { key: "today", label: "Today" },
              { key: "yesterday", label: "Yesterday" },
              { key: "week", label: "This week" },
              { key: "month", label: "This month" },
              { key: "custom", label: "Custom" },
            ].map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => {
                  setDateRange(r.key);
                  setShowCustom(r.key === "custom");
                }}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  dateRange === r.key
                    ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                    : "border-white/10 text-[#A0A0A0] hover:border-[#9D00FF]/40 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          )}
        </div>
        {showCustom && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-xs text-[#A0A0A0]">From</span>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:border-[#9D00FF] min-h-[36px]"
            />
            <span className="text-xs text-[#A0A0A0]">to</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:border-[#9D00FF] min-h-[36px]"
            />
          </div>
        )}

        {/* Ordering dashboard — the first screen a restaurant owner should see (AF-6). */}
        {orderingOn && (
          <OrderingDashboard daySummary={daySummary} topItems={topItems} liveData={liveData} navigate={navigate} />
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCards.map((c, i) => {
            const Icon = c.icon;
            const value = stats?.[c.key] ?? "—";
            const isBottleCard = !!c.panel;
            const isOpen = openPanel === c.panel;
            const activeStyle = isOpen
              ? c.panel === "active"
                ? "border-[#9D00FF]/60 bg-[#2D1B4E]/20"
                : "border-[#F59E0B]/60 bg-[#F59E0B]/5"
              : "border-white/10";

            return (
              <div
                key={c.key}
                data-testid={`stat-${c.key}`}
                style={{ animationDelay: `${i * 40}ms` }}
                onClick={isBottleCard ? () => togglePanel(c.panel) : undefined}
                className={`tb-fade-up bg-[#1A1A1A] border rounded-xl p-5 transition ${activeStyle} ${
                  isBottleCard ? "cursor-pointer hover:border-[#9D00FF]/40 select-none" : "hover:border-[#9D00FF]/40"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-wider text-[#A0A0A0]">{c.label}</div>
                  <div className="flex items-center gap-2">
                    <Icon size={18} className={c.color} />
                    {isBottleCard && (
                      isOpen
                        ? <ChevronUp size={14} className="text-[#A0A0A0]" />
                        : <ChevronDown size={14} className="text-[#A0A0A0]" />
                    )}
                  </div>
                </div>
                <div className="text-4xl font-black text-white">{value}</div>
                {isBottleCard && (
                  <div className="text-[10px] text-[#A0A0A0] mt-2 uppercase tracking-wider">
                    {isOpen ? "Click to collapse" : "Click to view"}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Active Tabs drill-down panel */}
        {bottleLockerOn && openPanel === "active" && (
          <DrillDownPanel
            title="All Active Tabs"
            tabs={activeTabs}
            accentColor="#9D00FF"
            borderColor="border-[#9D00FF]/30"
            onClose={() => setOpenPanel(null)}
            showStatus={true}
          />
        )}

        {/* Expiring Tabs drill-down panel */}
        {bottleLockerOn && openPanel === "expiring" && (
          <DrillDownPanel
            title="Tabs Expiring in 7 Days"
            tabs={expiring}
            accentColor="#F59E0B"
            borderColor="border-[#F59E0B]/30"
            onClose={() => setOpenPanel(null)}
            showStatus={false}
          />
        )}

        {/* Points cards — only shown when loyalty is enabled */}
        {loyaltyOn && (
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div data-testid="stat-points-issued" className="tb-fade-up bg-[#1A1A1A] border border-[#9D00FF]/20 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider text-[#A0A0A0]">Points Issued</div>
                <span className="text-[#9D00FF] text-lg">🏆</span>
              </div>
              <div className="text-4xl font-black text-[#9D00FF]">{stats?.points_issued_month ?? "—"}</div>
              <div className="text-xs text-[#A0A0A0] mt-1">Loyalty points earned by customers this period</div>
            </div>
            <div data-testid="stat-points-redeemed" className="tb-fade-up bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider text-[#A0A0A0]">Points Redeemed</div>
                <span className="text-emerald-300 text-lg">✓</span>
              </div>
              <div className="text-4xl font-black text-emerald-300">{stats?.points_redeemed_month ?? "—"}</div>
              <div className="text-xs text-[#A0A0A0] mt-1">Points redeemed against bills at the counter</div>
            </div>
          </div>
        )}

        {/* Ordering venues: link onward to Reports (the source of truth) — no numbers duplicated here */}
        {orderingOn && (
          <div className="mt-6">
            <Link to="/reports" className="text-sm text-[#9D00FF] hover:underline">
              View full reports →
            </Link>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
